import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";
import RecurrenceFields, { appendRecurrence, EMPTY_RECURRENCE } from "./RecurrenceFields";

export default function NewTeamOneOne() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [people, setPeople] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({
    teammate_id: "",
    scheduled_at: "",
    duration_min: 30,
    template_id: "",
    meeting_link: "",
    notes: "",
    ...EMPTY_RECURRENCE,
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/team").then((r) => {
      const owner = r.data?.owner;
      const members = r.data?.members || [];
      const all = [...(owner ? [owner] : []), ...members].filter((p) => p?.id && p.id !== user.id);
      setPeople(all);
    }).catch(() => setPeople([]));
    api.get("/meeting-templates?session_type=internal_1_1").then((r) => {
      const list = r.data || [];
      setTemplates(list);
      const def = list.find((x) => x.is_default) || list[0];
      if (def) setForm((f) => ({ ...f, template_id: f.template_id || def.id }));
    }).catch(() => setTemplates([]));
  }, [user.id]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const payload = {
        teammate_id: form.teammate_id,
        scheduled_at: new Date(form.scheduled_at).toISOString(),
        duration_min: Number(form.duration_min) || 30,
        template_id: form.template_id,
        meeting_link: form.meeting_link,
        notes: form.notes,
      };
      appendRecurrence(payload, form);
      const { data } = await api.post("/sessions/team-1-1", payload);
      navigate(`/app/team-1-1/${data.id}`);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  const canManage = user.role === "entrepreneur" && !user.team_owner_id;

  return (
    <div className="tb-page">
      <h1 className="tb-h1" data-testid="new-1-1-heading">{t("team1v1.bookTitle")}</h1>
      {people.length === 0 && (
        <p className="tb-muted" data-testid="no-teammates">{t("team1v1.noTeammates")}</p>
      )}
      <form className="tb-form" onSubmit={submit} data-testid="new-1-1-form">
        <div>
          <label>{t("team1v1.with")}</label>
          <select required value={form.teammate_id} onChange={onChange("teammate_id")} data-testid="select-teammate" disabled={people.length === 0}>
            <option value="">{people.length === 0 ? t("team1v1.noTeammates") : t("team1v1.pickTeammate")}</option>
            {people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} {p.company ? `· ${p.company}` : ""}
              </option>
            ))}
          </select>
        </div>
        <div className="tb-row">
          <div>
            <label>{t("book.when")}</label>
            <input type="datetime-local" required value={form.scheduled_at} onChange={onChange("scheduled_at")} data-testid="input-scheduled-at" />
          </div>
          <div>
            <label>{t("book.duration")}</label>
            <select value={form.duration_min} onChange={onChange("duration_min")} data-testid="input-duration">
              <option value={15}>15</option>
              <option value={30}>30</option>
              <option value={45}>45</option>
              <option value={60}>60</option>
            </select>
          </div>
        </div>
        <RecurrenceFields form={form} setForm={setForm} />
        <div>
          <label>{t("team1v1.template")}</label>
          <select required value={form.template_id} onChange={onChange("template_id")} data-testid="select-template">
            <option value="">{t("team1v1.pickTemplate")}</option>
            {templates.map((tpl) => (
              <option key={tpl.id} value={tpl.id}>
                {tpl.name}{tpl.is_default ? ` · ${t("settings.templates.default")}` : ""}
              </option>
            ))}
          </select>
          {canManage && (
            <Link to="/app/settings/templates" style={{ display: "inline-block", marginTop: 6, fontSize: "0.85rem" }}>
              {t("team1v1.manageTemplates")} →
            </Link>
          )}
        </div>
        <div>
          <label>{t("book.meetingLink")}</label>
          <input type="url" value={form.meeting_link} onChange={onChange("meeting_link")} data-testid="input-meeting-link" placeholder="https://meet.google.com/..." />
        </div>
        <div>
          <label>{t("team1v1.notes")}</label>
          <textarea value={form.notes} onChange={onChange("notes")} data-testid="input-notes" rows={3} />
        </div>
        {err && <div className="tb-form-error" data-testid="new-1-1-error">{err}</div>}
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" className="tb-ghost-btn" onClick={() => navigate("/app")} data-testid="btn-cancel">
            {t("book.cancel")}
          </button>
          <button type="submit" className="tb-primary-btn" disabled={loading || people.length === 0} data-testid="btn-create-1-1">
            {loading ? t("auth.loading") : t("team1v1.schedule")}
          </button>
        </div>
      </form>
    </div>
  );
}
